"use client";

import { ProcessingJobType, SocialPlatform } from "@prisma/client";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Components } from "@/app/(components)/_components/Components";
import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import { ModalType } from "@/app/(components)/generics/Modal/Modal";
import { useAuthUser } from "@/app/(components)/specifics/wrappers/AuthProvider/AuthProvider";
import {
  useCanEditContent,
  useCanManageWorkspace,
  useCanRunPaidActions,
} from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import PageWrapper from "@/app/(components)/specifics/wrappers/PageWrapper/PageWrapper";
import { useInitialSelection } from "@/app/(components)/specifics/wrappers/SelectionProvider/SelectionProvider";
import { generateProspectCriteria } from "@/app/actions/prospect-search/generate-criteria/action";
import { getProspectCriteria } from "@/app/actions/prospect-search/get/action";
import { updateProspectCriteriaField } from "@/app/actions/prospect-search/update-criteria/action";
import { useFetchOfferAvatars } from "@/app/hooks/fetchs/client-avatar/useFetchOfferAvatars";
import { useFetchImportedProfileCounts } from "@/app/hooks/fetchs/imported-profiles/useFetchImportedProfileCounts";
import { useFetchProjectOffers } from "@/app/hooks/fetchs/offers/useFetchProjectOffers";
import { useFetchStoredImportedBase } from "@/app/hooks/fetchs/prospect-search/useFetchImportedBase";
import { useFetchProspectCriteria } from "@/app/hooks/fetchs/prospect-search/useFetchProspectCriteria";
import { useFetchProspectingStats } from "@/app/hooks/fetchs/prospect-search/useFetchProspectingStats";
import { useFetchProspectSearchBootstrap } from "@/app/hooks/fetchs/prospect-search/useFetchProspectSearchBootstrap";
// Meidly order flow paused (card hidden): pending-order query disabled.
import { useFetchProspectSearchSummary } from "@/app/hooks/fetchs/prospect-search/useFetchProspectSearchSummary";
import { useFetchProspectSearchWorkspace } from "@/app/hooks/fetchs/prospect-search/useFetchProspectSearchWorkspace";
import { useFetchRunningJobs } from "@/app/hooks/fetchs/user/useFetchRunningJobs";
import { useCreditGuard } from "@/app/hooks/useCreditGuard";
import { useProspectsSection } from "@/app/hooks/useProspectsSection";
import { ModalStore } from "@/app/stores/ModalStore";
import { OfferStore } from "@/app/stores/OfferStore";
import { ProjectStore } from "@/app/stores/ProjectStore";
import { UserStore } from "@/app/stores/UserStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { CreditBucket } from "@/app/types/billing/CreditWallet";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { PageBootstrap } from "@/app/types/common/WorkspaceSelection";
import { ImportedProfileListTab } from "@/app/types/imported-profile/ImportedProfileListTab";
import { ProspectCriteriaJobMetadata } from "@/app/types/job/ProcessingJob";
import { ProspectSearchBootstrap } from "@/app/types/prospect/ProspectSearchBootstrap";
import {
  ProspectCriteriaField,
  ProspectSearchCriteria,
} from "@/app/types/prospect/ProspectSearchCriteria";
import { ProspectSearchData } from "@/app/types/prospect/ProspectSearchData";
import { UCookie } from "@/app/utils/UCookie";
import { ULogger } from "@/app/utils/ULogger";
import { ProcessingPrice } from "@/app/vars/Credits";
import { Limits } from "@/app/vars/Limits";
import { QueryKey } from "@/app/vars/QueryKeys";
import { CookieKey, WORKSPACE_COOKIE_MAX_AGE } from "@/app/vars/Storage";
import MessagesContentCard from "./components/MessagesContentCard/MessagesContentCard";
import NoAvatarCard from "./components/NoAvatarCard/NoAvatarCard";
import ProspectList from "./components/ProspectList/ProspectList";
import ProspectSources from "./components/ProspectSources/ProspectSources";
import ProspectStrategy from "./components/ProspectStrategy/ProspectStrategy";
import ProspectsSection from "./components/ProspectsSection/ProspectsSection";
import styles from "./ProspectSearch.module.css";

type Props = DefaultProps & {
  bootstrap: PageBootstrap<ProspectSearchBootstrap> | null;
};

export default function ProspectSearch(props: Props) {
  const T = useT();
  const { params, bootstrap } = props;
  const { lang } = params;

  const user = useAuthUser();
  const decrementCredits = UserStore((state) => state.decrementCredits);
  const project = ProjectStore((state) => state.currentProject);
  const projectsFetched = ProjectStore((state) => state.projectsFetched);
  const guardCredits = useCreditGuard(params);
  const canRunPaidActions = useCanRunPaidActions();
  const canEditContent = useCanEditContent();
  const canManageWorkspace = useCanManageWorkspace();
  const queryClient = useQueryClient();

  const initialSelection = useInitialSelection();

  const currentOfferId = OfferStore((state) => state.currentOfferId);
  const setStoreOffers = OfferStore((state) => state.setOffers);
  const selectedOfferId = currentOfferId ? String(currentOfferId) : "";

  const [selectedAvatarId, setSelectedAvatarId] = useState<string>(
    initialSelection.avatarId ? String(initialSelection.avatarId) : "",
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Collapse the offers -> avatars -> workspace chain into one round-trip on first load and
  // seed every downstream cache. The hooks below are gated on it so they read the seeds
  // instead of firing their own serialized fetches; offer/avatar switches still flow through
  // them afterwards (the selection effects converge on the same offer/avatar the server seeded).
  const initialBootstrap = bootstrap?.projectId === project?.id ? bootstrap?.data : undefined;

  const { isSuccess: bootstrapReady } = useFetchProspectSearchBootstrap({
    projectId: project?.id,
    enabled: !!user && !!project?.id,
    initialData: initialBootstrap,
  });

  const { data: fetchedOffers } = useFetchProjectOffers({
    projectId: project?.id,
    enabled: !!user && !!project?.id && bootstrapReady,
  });

  const offers = useMemo(() => fetchedOffers ?? [], [fetchedOffers]);
  const offersLoaded = fetchedOffers !== undefined;

  const currentOfferHasAvatar = offers.some(
    (offer) => offer.id === currentOfferId && (offer._count?.clientAvatars ?? 0) > 0,
  );

  // The extension consumes credits out-of-band; refresh the balance when the user
  // returns to this page (scoped here, not app-wide).
  useEffect(() => {
    const onFocus = () => {
      // The extension consumes credits and imports connections out-of-band. Refresh the
      // balance, then re-pull the avatar's workspace (summary/stats/counts/prospects) in a
      // single round-trip instead of letting each of those queries refetch on focus on its
      // own. Only the current avatar's workspace is active, so a prefix invalidation hits it.
      queryClient.invalidateQueries({ queryKey: [QueryKey.AUTH_USER] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.PROSPECT_SEARCH_WORKSPACE] });
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [queryClient]);

  useEffect(() => {
    const storedAvatar = UCookie.read(CookieKey.SELECTED_AVATAR);
    if (storedAvatar) {
      setSelectedAvatarId(storedAvatar);
    }
  }, []);

  const { data: fetchedAvatars } = useFetchOfferAvatars({
    offerId: selectedOfferId ? parseInt(selectedOfferId, 10) : undefined,
    enabled: !!user && !!selectedOfferId && bootstrapReady,
  });

  const avatars = useMemo(() => fetchedAvatars ?? [], [fetchedAvatars]);
  const avatarsLoaded = fetchedAvatars !== undefined;

  useEffect(() => {
    if (!avatarsLoaded) {
      return;
    }
    if (avatars.length === 0) {
      setSelectedAvatarId("");
      return;
    }
    setSelectedAvatarId(avatars[0] ? String(avatars[0].id) : "");
  }, [avatarsLoaded, avatars]);

  useEffect(() => {
    if (offersLoaded && project?.id) {
      setStoreOffers(project.id, offers);
    }
  }, [offersLoaded, offers, project?.id, setStoreOffers]);

  useEffect(() => {
    if (selectedAvatarId) {
      UCookie.write(CookieKey.SELECTED_AVATAR, selectedAvatarId, WORKSPACE_COOKIE_MAX_AGE);
    }
  }, [selectedAvatarId]);

  const selectedAvatarIdNum = selectedAvatarId ? parseInt(selectedAvatarId, 10) : undefined;

  const hasSelectableAvatars = currentOfferHasAvatar;
  const isAvatarResolved =
    !!selectedAvatarIdNum && avatars.some((a) => a.id === selectedAvatarIdNum);

  // Seeded by the bootstrap on first load; on an avatar switch it refetches the summary /
  // stats / counts / prospects for the new avatar in one round-trip and re-seeds them.
  const { isSuccess: workspaceReady, isLoading: isWorkspaceLoading } =
    useFetchProspectSearchWorkspace({
      avatarId: selectedAvatarIdNum,
      enabled: !!user && !!selectedAvatarIdNum && bootstrapReady,
    });

  const gateEnabled = !!user && !!selectedAvatarIdNum && workspaceReady;

  // Keep the full-page loader until the avatar is resolved AND its workspace data is ready,
  // so the strategy card and the prospect sources never flash in half-populated. Latched per
  // project so a later offer/avatar switch doesn't fall back to it (handled by inline loaders).
  const readyRef = useRef(false);
  useEffect(() => {
    readyRef.current = false;
  }, []);

  const projectReady =
    bootstrapReady && (!hasSelectableAvatars || (isAvatarResolved && workspaceReady));
  const isReady = readyRef.current || (projectsFetched && (!project?.id || projectReady));

  useEffect(() => {
    if (isReady) {
      readyRef.current = true;
    }
  }, [isReady]);

  const { data: searchSummary } = useFetchProspectSearchSummary({
    avatarId: selectedAvatarIdNum,
    enabled: gateEnabled,
  });

  const hasCriteria = searchSummary?.hasCriteria ?? false;
  const needUpdate = searchSummary?.needUpdate ?? false;

  // Default collapsed when criteria exist (then lazy-fetched on expand); a manual toggle wins until the avatar changes.
  const [strategyManualToggle, setStrategyManualToggle] = useState<boolean | null>(null);

  // Drop any manual expand and fall back to the per-avatar default whenever the selected avatar or
  // offer changes, so the card collapses on switch instead of staying open on the previous data.
  useEffect(() => {
    setStrategyManualToggle(null);
  }, []);

  const strategyOpen = strategyManualToggle ?? (searchSummary ? !hasCriteria : false);
  const toggleStrategy = () => setStrategyManualToggle(!strategyOpen);

  const {
    data: prospectData,
    isLoading: isCriteriaLoading,
    isFetching: isCriteriaFetching,
  } = useFetchProspectCriteria({
    avatarId: selectedAvatarIdNum,
    enabled: !!user && !!selectedAvatarIdNum && strategyOpen && hasCriteria,
  });

  const criteria = prospectData?.criteria ?? null;

  const { data: runningJobs, isLoading: isRunningJobsLoading } = useFetchRunningJobs({
    enabled: !!user,
  });

  const { data: socialImportedProfileCounts, isLoading: isCountsLoading } =
    useFetchImportedProfileCounts({
      avatarId: selectedAvatarIdNum,
      enabled: gateEnabled,
      refetchOnWindowFocus: false,
    });

  const linkedinCount = socialImportedProfileCounts?.[SocialPlatform.LINKEDIN] ?? 0;

  const { data: importedBase, isFetching: importedBaseFetching } = useFetchStoredImportedBase({
    avatarId: selectedAvatarIdNum,
    enabled: gateEnabled,
  });

  const { data: prospectingStats, isLoading: isStatsLoading } = useFetchProspectingStats({
    avatarId: selectedAvatarIdNum,
    enabled: gateEnabled,
    refetchOnWindowFocus: false,
  });

  const isGenerating = useMemo(() => {
    if (!runningJobs || !selectedAvatarIdNum) return false;
    return runningJobs.some((job) => {
      if (job.type !== ProcessingJobType.PROSPECT_SEARCH) return false;
      const metadata = job.metadata as ProspectCriteriaJobMetadata | null;
      return metadata?.avatarId === selectedAvatarIdNum;
    });
  }, [runningJobs, selectedAvatarIdNum]);

  useEffect(() => {
    if (isGenerating) {
      setIsSubmitting(false);
    }
  }, [isGenerating]);

  // Bridge the gap between a generation finishing (job leaves the running list)
  // and the freshly generated criteria landing in cache. Without it, the render
  // in between briefly shows the "generate" button before the columns appear.
  const [awaitingCriteria, setAwaitingCriteria] = useState(false);
  const wasCriteriaFetching = useRef(false);

  useEffect(() => {
    if (isGenerating || isSubmitting) {
      setAwaitingCriteria(true);
    }
  }, [isGenerating, isSubmitting]);

  useEffect(() => {
    if (hasCriteria) {
      setAwaitingCriteria(false);
    }
  }, [hasCriteria]);

  useEffect(() => {
    if (wasCriteriaFetching.current && !isCriteriaFetching) {
      setAwaitingCriteria(false);
    }
    wasCriteriaFetching.current = isCriteriaFetching;
  }, [isCriteriaFetching]);

  // Meidly order flow paused (its card is hidden): skip the pending-order query.

  const isSourcesLoading = isStatsLoading || isCountsLoading || isWorkspaceLoading;

  const prospectsSection = useProspectsSection({
    params,
    avatarId: selectedAvatarIdNum,
    projectId: project?.id,
    enabled: gateEnabled && hasCriteria,
    isRefreshing: prospectingStats?.linkedinPending ?? false,
  });

  const wasLinkedinPending = useRef(false);

  useEffect(() => {
    const pending = prospectingStats?.linkedinPending ?? false;

    if (wasLinkedinPending.current && !pending) {
      queryClient.invalidateQueries({ queryKey: [QueryKey.CLIENT_PROSPECTS] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.USER_IMPORTED_PROFILES] });
    }

    wasLinkedinPending.current = pending;
  }, [prospectingStats?.linkedinPending, queryClient]);

  // Keep the strategy in its loading state (never the empty "generate" block) until the current
  // avatar's criteria state is actually known: no summary yet for this avatar (e.g. right after
  // switching avatar/offer in the select), or the summary says criteria exist but the lazy criteria
  // query hasn't populated them yet.
  const isStrategyLoading =
    !selectedAvatarIdNum ||
    isRunningJobsLoading ||
    !searchSummary ||
    isCriteriaLoading ||
    (strategyOpen && hasCriteria && !criteria);

  // Criteria are lazy-fetched on expand; pull them on demand (shared cache) for the
  // modals that need them even while the strategy card stays collapsed.
  const ensureCriteria = async (): Promise<ProspectSearchCriteria | null> => {
    if (!selectedAvatarIdNum) {
      return null;
    }
    const data = await queryClient.ensureQueryData<ProspectSearchData | null>({
      queryKey: [QueryKey.PROSPECT_CRITERIA, selectedAvatarIdNum],
      queryFn: () => getProspectCriteria(selectedAvatarIdNum),
    });
    return data?.criteria ?? null;
  };

  const runGenerateCriteria = async (offerId: number, avatarId: number, cost: number) => {
    setStrategyManualToggle(true);
    setIsSubmitting(true);
    try {
      await generateProspectCriteria(offerId, avatarId);
      decrementCredits(cost, CreditBucket.PROSPECT);
      queryClient.invalidateQueries({ queryKey: [QueryKey.RUNNING_JOBS] });
    } catch (error) {
      ULogger.error("[ProspectSearch] Failed to generate criteria", error);
      setIsSubmitting(false);
    }
  };

  const handleGenerateCriteria = () => {
    if (!user) {
      ModalStore.getState().openSignUp(params);
      return;
    }

    if (!selectedOfferId || !selectedAvatarIdNum) return;

    const cost = ProcessingPrice[ProcessingJobType.PROSPECT_SEARCH];
    if (!guardCredits(cost)) {
      return;
    }

    const offerId = parseInt(selectedOfferId, 10);
    const avatarId = selectedAvatarIdNum;

    ModalStore.getState().openModal(
      Components.CostConfirm,
      T(
        hasCriteria
          ? TKey.PROSPECT_CRITERIA_REGENERATE_TITLE
          : TKey.PROSPECT_CRITERIA_CONFIRM_TITLE,
      ) as string,
      {
        params,
        cost: "",
        ctaText: T(
          hasCriteria
            ? TKey.PROSPECT_SEARCH_REGENERATE_CRITERIA_CTA
            : TKey.PROSPECT_SEARCH_GENERATE_CRITERIA_CTA,
        ) as string,
        description: T(
          hasCriteria
            ? TKey.PROSPECT_CRITERIA_REGENERATE_DESC
            : TKey.PROSPECT_CRITERIA_CONFIRM_DESC,
        ) as string,
        onConfirm: () => runGenerateCriteria(offerId, avatarId, cost),
      },
      ModalType.SMALL,
    );
  };

  const handleViewFound = () => {
    if (!selectedAvatarIdNum) return;
    ModalStore.getState().openProspectsFound(params, selectedAvatarIdNum);
  };

  const handleLinkedinExtract = () => {
    if (!user) {
      ModalStore.getState().openSignUp(params);
      return;
    }
    if (!selectedAvatarIdNum || !hasCriteria) return;
    if (!guardCredits(1, CreditBucket.PROSPECT)) return;

    ModalStore.getState().openImportedProfileExtensionGuide(params);
  };

  const handleOpenImportedBase = () => {
    if (!selectedAvatarIdNum) return;

    const avatarId = selectedAvatarIdNum;

    ModalStore.getState().openModal(
      Components.ImportedBase,
      T(TKey.IMPORTED_BASE_MODAL_TITLE) as string,
      {
        avatarId,
        onAdded: () => {
          queryClient.invalidateQueries({ queryKey: [QueryKey.IMPORTED_BASE, avatarId] });
          queryClient.invalidateQueries({ queryKey: [QueryKey.USER_IMPORTED_PROFILES] });
          queryClient.invalidateQueries({ queryKey: [QueryKey.PROSPECTING_STATS, avatarId] });
          queryClient.invalidateQueries({
            queryKey: [QueryKey.SOCIAL_IMPORTED_PROFILE_COUNTS, avatarId],
          });
          queryClient.invalidateQueries({
            queryKey: [QueryKey.PROSPECT_SEARCH_WORKSPACE, avatarId],
          });
        },
      },
      ModalType.SMALL,
    );
  };

  const handleViewImportedProfiles = async (tab?: ImportedProfileListTab) => {
    if (!selectedAvatarIdNum) return;
    const criteriaData = await ensureCriteria();
    ModalStore.getState().openImportedProfilesList(
      params,
      selectedAvatarIdNum,
      criteriaData,
      tab,
      () => {
        prospectsSection.resetPage();
        queryClient.invalidateQueries({
          queryKey: [QueryKey.CLIENT_PROSPECTS, selectedAvatarIdNum],
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKey.PROSPECTING_STATS, selectedAvatarIdNum],
        });
      },
    );
  };

  const handleSaveField = (field: ProspectCriteriaField, fieldItems: string[]) => {
    if (!selectedAvatarIdNum) return;

    queryClient.setQueryData<ProspectSearchData | null>(
      [QueryKey.PROSPECT_CRITERIA, selectedAvatarIdNum],
      (old) =>
        old?.criteria ? { ...old, criteria: { ...old.criteria, [field]: fieldItems } } : old,
    );

    updateProspectCriteriaField(selectedAvatarIdNum, field, fieldItems)
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: [QueryKey.PROSPECT_SEARCH_SUMMARY, selectedAvatarIdNum],
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKey.IMPORTED_BASE, selectedAvatarIdNum],
        });
      })
      .catch((error) => {
        ULogger.error("[ProspectSearch] Failed to update criteria", error);
        queryClient.invalidateQueries({
          queryKey: [QueryKey.PROSPECT_CRITERIA, selectedAvatarIdNum],
        });
      });
  };

  const renderBody = () => {
    if (!user) {
      const openSignUp = () => ModalStore.getState().openSignUp(params);
      return (
        <>
          <ProspectStrategy lang={lang} isPlaceholder={true} onGenerate={handleGenerateCriteria} />
          <ProspectSources lang={lang} isPlaceholder={true} onLinkedinAnalyze={openSignUp} />
          <ProspectList
            lang={lang}
            isConnected={false}
            onQualify={openSignUp}
            onReject={openSignUp}
          />
          <MessagesContentCard params={params} />
        </>
      );
    }

    if (!isReady) {
      return (
        <div className={styles.loaderContainer}>
          <PointsLoader color="var(--color-green-3)" delay={Limits.PROSPECT_SEARCH_LOADER_DELAY} />
        </div>
      );
    }

    if (!currentOfferHasAvatar) {
      return <NoAvatarCard params={params} />;
    }

    return (
      <>
        <ProspectStrategy
          lang={lang}
          criteria={criteria}
          hasCriteria={hasCriteria}
          needUpdate={needUpdate}
          avatarUpdatedAt={searchSummary?.avatarUpdatedAt ?? null}
          isGenerating={isGenerating || isSubmitting || awaitingCriteria}
          isLoading={isStrategyLoading}
          isOpen={strategyOpen}
          onToggle={toggleStrategy}
          onGenerate={canRunPaidActions ? handleGenerateCriteria : undefined}
          onRegenerate={canRunPaidActions ? handleGenerateCriteria : undefined}
          onSaveField={canEditContent ? handleSaveField : undefined}
        />
        {canManageWorkspace && (
          <ProspectSources
            lang={lang}
            canRun={canRunPaidActions}
            isLoading={isSourcesLoading}
            importedBase={importedBase}
            importedBaseRunning={importedBaseFetching || !!importedBase?.refreshing}
            onOpenImportedBase={
              canEditContent && selectedAvatarIdNum ? handleOpenImportedBase : undefined
            }
            linkedinCount={linkedinCount}
            linkedinPotentialCount={prospectingStats?.linkedinPotentialCount ?? 0}
            linkedinFoundCount={prospectingStats?.linkedinFoundCount ?? 0}
            linkedinRejectedCount={prospectingStats?.linkedinRejectedCount ?? 0}
            linkedinCanAnalyze={hasCriteria}
            linkedinPending={prospectingStats?.linkedinPending ?? false}
            onLinkedinAnalyze={handleLinkedinExtract}
            onLinkedinViewImportedProfiles={handleViewImportedProfiles}
            onViewFound={handleViewFound}
          />
        )}
        {hasCriteria && !isGenerating && (
          <>
            <ProspectsSection
              params={params}
              avatarId={selectedAvatarIdNum}
              state={prospectsSection}
              canAdd={canOrder}
            />

            {prospectsSection.hasList && <MessagesContentCard params={params} />}
          </>
        )}
      </>
    );
  };

  const canOrder =
    !!user && canEditContent && hasCriteria && !isGenerating && !!selectedAvatarIdNum;

  return (
    <PageWrapper title={T(TKey.PROSPECT_SEARCH_TITLE)} subtitle={T(TKey.PROSPECT_SEARCH_SUBTITLE)}>
      <div className={styles.mainBody}>{renderBody()}</div>
    </PageWrapper>
  );
}
